/**
 * PURPOSE: Estado compartido del Centro de Programación (FTR-0028 WG3, D-23).
 * RESPONSIBILITIES: Unir los datos del servidor con el estado de pantalla — vista activa, filtros,
 *   selección, edición inline y panel abierto — y ofrecer un único ejecutor de escrituras.
 *
 *   UNA FUENTE, CUATRO PROYECCIONES. Aquí viven `rows` (ya normalizadas), `filters` y `selection`.
 *   Las vistas no guardan nada propio: reciben las mismas filas filtradas y solo cambian cómo las
 *   agrupan. Por eso cambiar de vista conserva filtros y selección, y un cambio hecho en una se ve
 *   en las otras tres sin sincronizar nada.
 *
 *   UN SOLO CAMINO DE ESCRITURA. `execute` pone la versión vigente del borrador, traduce el error
 *   del backend, avisa y refresca. Que sea uno solo es lo que garantiza que ninguna acción se salte
 *   el `If-Match` ni deje la pantalla mostrando un estado que el servidor no confirmó.
 * COLLABORATORS: hooks/useProgrammingCenterData.js, utils/programmingCenter*.js, las cuatro vistas
 *   y los paneles.
 * ROUTE: winscore/src/area/FasesArea/ProgrammingCenter/ProgrammingCenterProvider.jsx
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "react-hot-toast";

import { useProgrammingCenterData } from "../../../hooks/useProgrammingCenterData.js";
import {
  buildContextIndex,
  buildRowViewModels,
  summarizeRows,
} from "../../../utils/programmingCenterModel.js";
import {
  EMPTY_FILTERS,
  buildFilterOptions,
  filterRows,
  hasActiveFilters,
  indexConflicts,
} from "../../../utils/programmingCenterGrouping.js";
import {
  buildCourtsByVenue,
  buildScheduleDraft,
  computeDirtyScheduleEntries,
} from "../../../utils/programmingCenterEdit.js";
import { parseProgrammingCenterError } from "../../../utils/programmingCenterErrors.js";
import useProgrammingCenterActions from "./useProgrammingCenterActions.js";

export const VIEW_MODES = Object.freeze({
  GROUP: "group",
  DATE: "date",
  TIMELINE: "timeline",
  VENUE: "venue",
});

export const VIEW_OPTIONS = Object.freeze([
  { value: VIEW_MODES.GROUP, label: "Por Grupo" },
  { value: VIEW_MODES.DATE, label: "Por Fecha" },
  { value: VIEW_MODES.TIMELINE, label: "Calendario" },
  { value: VIEW_MODES.VENUE, label: "Por Sede" },
]);

const ProgrammingCenterContext = createContext(null);

export const useProgrammingCenter = () => {
  const value = useContext(ProgrammingCenterContext);
  if (!value) {
    throw new Error("useProgrammingCenter debe usarse dentro de ProgrammingCenterProvider.");
  }
  return value;
};

export const ProgrammingCenterProvider = ({ idevent, idsport, enabled = true, children }) => {
  const data = useProgrammingCenterData({ idevent, idsport, enabled });

  const [view, setView] = useState(VIEW_MODES.GROUP);
  const [filters, setFilters] = useState({ ...EMPTY_FILTERS });
  const [selection, setSelection] = useState(() => new Set());
  const [panel, setPanel] = useState(null);
  const [busy, setBusy] = useState(null);
  const [scheduleDraft, setScheduleDraft] = useState({});
  const [timelineDate, setTimelineDate] = useState(null);

  const index = useMemo(() => buildContextIndex(data.context), [data.context]);

  const rows = useMemo(
    () => buildRowViewModels(data.draft?.rows || [], index),
    [data.draft, index]
  );

  const rowsById = useMemo(() => {
    const map = new Map();
    for (const row of rows) if (row.iddraftmatch) map.set(row.iddraftmatch, row);
    return map;
  }, [rows]);

  const visibleRows = useMemo(() => filterRows(rows, filters), [rows, filters]);
  const summary = useMemo(() => summarizeRows(rows), [rows]);
  const filterOptions = useMemo(() => buildFilterOptions(rows, data.context), [rows, data.context]);
  const conflictsByKey = useMemo(() => indexConflicts(data.draft?.timeline), [data.draft]);
  const courtsByVenue = useMemo(() => buildCourtsByVenue(data.context), [data.context]);

  // El borrador de edición inline se resiembra con cada respuesta del servidor. Lo que el servidor
  // acaba de confirmar es la nueva base: mantener ediciones "sucias" sobre datos que ya cambiaron
  // haría que el siguiente lote enviara diferencias contra un original inexistente.
  const draftSignature = data.draft?.version ?? null;
  const lastSignature = useRef(null);
  useEffect(() => {
    if (lastSignature.current === draftSignature && Object.keys(scheduleDraft).length) return;
    lastSignature.current = draftSignature;
    setScheduleDraft(buildScheduleDraft(rows));
    // `rows` cambia de identidad en cada refetch; la firma es la versión, que es lo que de verdad
    // indica que el servidor escribió algo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftSignature, rows.length]);

  // Una fila que deja de existir o de ser seleccionable no puede seguir seleccionada: el lote la
  // arrastraría y el backend la rechazaría entera.
  useEffect(() => {
    setSelection((current) => {
      if (!current.size) return current;
      const next = new Set();
      for (const id of current) {
        const row = rowsById.get(id);
        if (row?.canSelect) next.add(id);
      }
      return next.size === current.size ? current : next;
    });
  }, [rowsById]);

  const dirtyEntries = useMemo(
    () => computeDirtyScheduleEntries(visibleRows, scheduleDraft),
    [visibleRows, scheduleDraft]
  );

  const setFilter = useCallback((key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
  }, []);

  const resetFilters = useCallback(() => setFilters({ ...EMPTY_FILTERS }), []);

  const toggleSelection = useCallback(
    (iddraftmatch) => {
      const row = rowsById.get(iddraftmatch);
      if (!row?.canSelect) return;
      setSelection((current) => {
        const next = new Set(current);
        if (next.has(iddraftmatch)) next.delete(iddraftmatch);
        else next.add(iddraftmatch);
        return next;
      });
    },
    [rowsById]
  );

  /** Selección por bloque (cabecera de grupo, jornada o sede). Los BYE nunca entran (D-20). */
  const setBlockSelection = useCallback((blockRows = [], selected) => {
    setSelection((current) => {
      const next = new Set(current);
      for (const row of blockRows) {
        if (!row.canSelect) continue;
        if (selected) next.add(row.iddraftmatch);
        else next.delete(row.iddraftmatch);
      }
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelection(new Set()), []);

  const selectedRows = useMemo(
    () => visibleRows.filter((row) => selection.has(row.iddraftmatch)),
    [visibleRows, selection]
  );

  /**
   * El único camino de escritura. Inyecta la versión vigente, traduce el error y refresca. Ante un
   * conflicto de concurrencia recarga TODO y no reintenta: lo aprobado ya no es lo que pasaría.
   */
  const execute = useCallback(
    async (label, work, { refreshScope = "draft", successMessage = null } = {}) => {
      setBusy(label);
      try {
        const result = await work({
          idevent: data.scope.idevent,
          idsport: data.scope.idsport,
          version: data.version,
        });
        await data.refresh({ scope: refreshScope });
        if (successMessage) toast.success(successMessage);
        return { ok: true, data: result };
      } catch (error) {
        const parsed = parseProgrammingCenterError(error);
        toast.error(parsed.message);
        if (parsed.requiresReload) await data.refresh({ scope: "structure" });
        return { ok: false, error: parsed };
      } finally {
        setBusy(null);
      }
    },
    [data]
  );

  const actions = useProgrammingCenterActions({
    execute,
    courtsByVenue,
    dirtyEntries,
    rows,
    selection,
    clearSelection,
  });

  const value = useMemo(
    () => ({
      ...data,
      view,
      setView,
      filters,
      setFilter,
      resetFilters,
      hasFilters: hasActiveFilters(filters),
      filterOptions,
      rows,
      rowsById,
      visibleRows,
      selectedRows,
      selection,
      toggleSelection,
      setBlockSelection,
      clearSelection,
      summary,
      conflictsByKey,
      courtsByVenue,
      index,
      scheduleDraft,
      setScheduleDraft,
      dirtyEntries,
      panel,
      setPanel,
      busy,
      execute,
      actions,
      timelineDate,
      setTimelineDate,
    }),
    [
      data,
      actions,
      view,
      filters,
      setFilter,
      resetFilters,
      filterOptions,
      rows,
      rowsById,
      visibleRows,
      selectedRows,
      selection,
      toggleSelection,
      setBlockSelection,
      clearSelection,
      summary,
      conflictsByKey,
      courtsByVenue,
      index,
      scheduleDraft,
      dirtyEntries,
      panel,
      busy,
      execute,
      timelineDate,
    ]
  );

  return (
    <ProgrammingCenterContext.Provider value={value}>{children}</ProgrammingCenterContext.Provider>
  );
};

export default ProgrammingCenterProvider;
